import React, { useEffect, useState } from 'react';
import { Loader2, MoreHorizontal } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { GENERATE_AUTH_TOKEN } from '../../config';

interface PlanProps {
  profile: {
    id_company: string | null;
  };
  setError: (error: string | null) => void;
  setSuccess: (success: string | null) => void;
}

const PLAN_LABELS: Record<string, string> = {
  gratis: 'Prueba Gratis',
  emprendimiento: 'Plan Básico',
  negocio: 'Plan Pro',
  enterprise: 'Plan Ultra',
  enterprise1: 'Plan Ultra Nivel 1',
  enterprise2: 'Plan Ultra Nivel 2',
  enterprise3: 'Plan Ultra Nivel 3',
};

function getCurrentMonthRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  return {
    start: start.toISOString(),
    end: end.toISOString(),
  };
}

// Get current billing cycle range based on an anchor day (paid_plan_start_date)
// Handles months with fewer days (29/30/31) by using the last day of the month when needed
function getBillingCycleRange(paidPlanStartDate: string, upgradePlanDate?: string | null) {
  const now = new Date();
  const subscriptionDate = new Date(paidPlanStartDate);
  const referenceDate = upgradePlanDate ? new Date(upgradePlanDate) : subscriptionDate;
  const anchorDay = referenceDate.getDate();

  const daysInMonth = (year: number, month: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const anchorFor = (year: number, month: number) => {
    const dim = daysInMonth(year, month);
    const day = Math.min(anchorDay, dim);
    return new Date(year, month, day, 0, 0, 0, 0);
  };

  // Candidate start for the current month
  let start = anchorFor(now.getFullYear(), now.getMonth());

  // If the anchor day this month is in the future, use previous month's anchor
  if (start.getTime() > now.getTime()) {
    const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    start = anchorFor(prevMonth.getFullYear(), prevMonth.getMonth());
  }

  // Do not start before actual subscription start date (first cycle starts on paid_plan_start_date)
  if (start.getTime() < subscriptionDate.getTime()) {
    start = new Date(subscriptionDate.getFullYear(), subscriptionDate.getMonth(), subscriptionDate.getDate(), 0, 0, 0, 0);
  }

  // End is the next cycle's start
  const nextMonth = new Date(start.getFullYear(), start.getMonth() + 1, 1);
  const end = anchorFor(nextMonth.getFullYear(), nextMonth.getMonth());

  return {
    start: start.toISOString(),
    end: end.toISOString(),
    nextRenewal: end,
  };
}

export const Plan: React.FC<PlanProps> = ({ profile, setError }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState<string>('');
  const [maxFotosMes, setMaxFotosMes] = useState<number>(0);
  const [usedFotos, setUsedFotos] = useState<number>(0);
  const [fotosGratisRestantes, setFotosGratisRestantes] = useState<number>(0);
  const [imagenesRestantes, setImagenesRestantes] = useState<number>(0);
  const [bonusImagenes, setBonusImagenes] = useState<number>(0);
  const [preapprovalId, setPreapprovalId] = useState<string | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const [renewalDate, setRenewalDate] = useState<Date | null>(null);
  const [pais, setPais] = useState<string>('');
  const [openingPortal, setOpeningPortal] = useState(false);

  useEffect(() => {
    const fetchPlanInfo = async () => {
      if (!profile.id_company) return;
      try {
        setLoading(true);
        // 1. Get plan, max_fotos_mes, subscription info, and paid_plan_start_date from public.company_profiles
        const { data: company, error: companyError } = await supabase
          .from('company_profiles')
          .select('plan, max_fotos_mes, preapproval_id, status, imagenes_restantes, paid_plan_start_date, upgrade_plan_date, pais')
          .eq('id_company', profile.id_company)
          .single();
        if (companyError) throw companyError;
        setPlan(company.plan);
        setMaxFotosMes(company.max_fotos_mes);
        setPreapprovalId(company.preapproval_id);
        setSubscriptionStatus(company.status);
        const remaining = typeof company.imagenes_restantes === 'number' ? company.imagenes_restantes : 0;
        if (company.plan === 'gratis') {
          setFotosGratisRestantes(remaining);
          setImagenesRestantes(remaining);
          setUsedFotos(0);
          setBonusImagenes(0);
        } else {
          setFotosGratisRestantes(0);
          setImagenesRestantes(remaining);
          if (company.max_fotos_mes && company.max_fotos_mes > 0) {
            if (remaining > company.max_fotos_mes) {
              // User has bonus images
              setBonusImagenes(remaining - company.max_fotos_mes);
              setUsedFotos(0);
            } else {
              setBonusImagenes(0);
              const used = company.max_fotos_mes - remaining;
              setUsedFotos(used);
            }
          } else {
            setUsedFotos(0);
            setBonusImagenes(0);
          }
        }
        if (company.pais) setPais(company.pais);

        // 2. Get count of generated photos in the current billing cycle (only for paid plans)
        // While on free trial, keep current behavior (handled below)
        if (company.plan !== 'gratis') {
          const subscriptionDate = company.paid_plan_start_date ? new Date(company.paid_plan_start_date) : null;

          let nextRenewalLocal: Date | null = null;

          if (subscriptionDate) {
            const { nextRenewal } = getBillingCycleRange(company.paid_plan_start_date, company.upgrade_plan_date);
            nextRenewalLocal = nextRenewal;
          } else {
            // Fallback to calendar month if no paid start date is set
            const { end } = getCurrentMonthRange();
            // Renewal date is the first day of next month at 00:00
            nextRenewalLocal = new Date(end);
          }
          setRenewalDate(nextRenewalLocal);
        } else {
          setRenewalDate(null);
        }
      } catch (err: any) {
        setError(err.message || 'Error al cargar el plan');
      } finally {
        setLoading(false);
      }
    };
    if (profile?.id_company) {
      fetchPlanInfo();
    }
  }, [profile?.id_company, setError]);

  if (!profile?.id_company) {
    return (
      <div className="bg-surface-card rounded-lg border border-neutral-200 p-5">
        <h2 className="text-[17px] font-semibold text-brand-black/90 mb-3">Plan</h2>
        <p className="text-[15px] text-neutral-500">No se encontró información de empresa.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
      </div>
    );
  }

  const handleChangePlan = async () => {
    const isArgentina = (pais || '').toLowerCase() === 'argentina';
    if (!isArgentina && plan && plan !== 'gratis') {
      try {
        setOpeningPortal(true);
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Usuario no autenticado');
        const resp = await fetch('https://ai-agency-dt7d.onrender.com/webhook/billing-portal', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': GENERATE_AUTH_TOKEN },
          body: JSON.stringify({ user_id: user.id })
        });
        if (!resp.ok) throw new Error('No se pudo abrir el portal de facturación');
        const data = await resp.json();
        if (data?.url) {
          window.location.href = data.url as string;
        } else {
          throw new Error('URL inválida recibida');
        }
      } catch (e: any) {
        setError(e?.message || 'Error al abrir el portal de facturación');
        setOpeningPortal(false);
      }
    } else {
      navigate('/subscription');
    }
  };

  return (
    <div className="bg-surface-card rounded-lg border border-neutral-200 p-5">
      <h2 className="text-[17px] font-semibold text-brand-black/90 mb-4">Plan</h2>
      <div className="mb-4">
        <span className="text-[15px] text-neutral-500">Plan actual: </span>
        <span className="text-[15px] font-semibold text-brand-primary">{PLAN_LABELS[plan] || plan}</span>
        {subscriptionStatus && plan !== 'gratis' && (
          <div className="mt-1.5">
            <span className="text-[13px] font-medium px-2 py-0.5 rounded-sm bg-green-50 border border-green-200 text-green-700">
              Suscripción activa
            </span>
          </div>
        )}
      </div>
      <div className="mb-2">
        {plan === 'gratis' ? (
          <span className="text-[15px] text-neutral-500">
            Fotos restantes de la prueba gratis: <span className="font-semibold text-brand-black/90">{fotosGratisRestantes} disponibles</span>
          </span>
        ) : (
          <span className="text-[15px] text-neutral-500">
            Te quedan <span className="font-semibold text-brand-black/90">{imagenesRestantes} imágenes</span> para generar en este periodo
          </span>
        )}
      </div>
      <div className="w-full bg-neutral-100 rounded-full h-[5px] mb-2 overflow-hidden">
        {plan === 'gratis' ? (
          <div
            className="bg-brand-primary h-[5px] rounded-full"
            style={{ width: `${Math.min(maxFotosMes > 0 ? (fotosGratisRestantes / maxFotosMes) * 100 : (fotosGratisRestantes > 0 ? 100 : 0), 100)}%` }}
          />
        ) : (
          <div className="flex h-[5px]">
            <div
              className="bg-brand-primary h-[5px]"
              style={{ 
                width: `${Math.min(maxFotosMes > 0 ? (Math.min(imagenesRestantes, maxFotosMes) / maxFotosMes) * 100 : 0, 100)}%`,
                borderRadius: bonusImagenes > 0 ? '9999px 0 0 9999px' : '9999px'
              }}
            />
            {bonusImagenes > 0 && (
              <div
                className="bg-green-500 h-[5px] rounded-r-full"
                style={{ 
                  width: `${Math.min((bonusImagenes / maxFotosMes) * 100, 50)}%`
                }}
              />
            )}
          </div>
        )}
      </div>
      <div className="text-[13px] text-neutral-500 mb-4">
        {plan === 'gratis' ? (
          fotosGratisRestantes > 0
            ? `Te quedan ${fotosGratisRestantes} fotos gratuitas disponibles.`
            : 'Has utilizado todas tus fotos gratuitas.'
        ) : (
          bonusImagenes > 0
            ? `Tienes ${maxFotosMes} imágenes de tu plan y ${bonusImagenes} imágenes de bonus adicional.`
            : `Generaste ${usedFotos} de ${maxFotosMes} imágenes este periodo.`
        )}
      </div>
      {plan !== 'gratis' && renewalDate && (
        <div className="text-[13px] text-neutral-500 mb-4">
          Próxima renovación de imágenes: {renewalDate.toLocaleDateString('es-AR', { day: 'numeric', month: 'long', year: 'numeric' })}
        </div>
      )}
      
      {/* Subscription details if available */}
      {preapprovalId && subscriptionStatus === 'active' && (
        <div className="mb-4 p-3 bg-surface-elevated border border-neutral-200 rounded-md">
          <p className="text-[13px] text-neutral-500 mb-0.5">ID de suscripción: <span className="font-mono text-[13px] text-brand-black/90">{preapprovalId}</span></p>
          <p className="text-[13px] text-neutral-500">Tu suscripción se renovará automáticamente a través de Mercado Pago.</p>
        </div>
      )}
      
      <button
        onClick={handleChangePlan}
        disabled={openingPortal}
        className="w-full sm:w-auto inline-flex items-center justify-center gap-2 h-10 px-4 bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-70 text-white text-[15px] font-medium rounded-md shadow-sm transition-colors"
      >
        {openingPortal ? <Loader2 className="w-4 h-4 animate-spin" /> : <MoreHorizontal className="w-4 h-4" />}
        {((pais || '').toLowerCase() !== 'argentina' && plan && plan !== 'gratis') ? 'Gestionar subscripción' : 'Ver planes'}
      </button>
    </div>
  );
};

export default Plan;
